import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { ESkillType } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import createSkill from '@src/APIs/graphQl/Skill/createSkill';
import Dialog from '@src/App/Components/Dialogs/Dialog/Dialog';
import type SkillDetail from '@src/Objects/SkillDetail';
import type { AppState } from '@store/store';
import { Hand } from 'lucide-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

interface CreateSimpleSkillDialogProps {
    open: boolean;
    onClose: () => void;
    onSkillCreated: (skill: SkillDetail) => void;
}

export default function CreateSimpleSkillDialog({ open, onClose, onSkillCreated }: CreateSimpleSkillDialogProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).skills;
    const skillCategories = useSelector((state: AppState) => state.company.skillCategories);
    const offline = useSelector((state: AppState) => state.client.offline);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    // Auto-assign to default category - Admin will categorize later
    const defaultCategoryId =
        skillCategories.find((cat) => cat.parentCategory === null)?.id || skillCategories[0]?.id || 1;

    console.log('Available skill categories:', skillCategories);
    console.log('Selected default category ID:', defaultCategoryId);
    console.log(
        'Skill categories structure:',
        skillCategories.map((cat) => ({ id: cat.id, title: cat.title, parentCategory: cat.parentCategory })),
    );
    const [skillType, setSkillType] = useState<ESkillType>(ESkillType.HARD_SKILL);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async () => {
        if (!title.trim()) {
            setError('Bitte geben Sie einen Skill-Namen ein');
            return;
        }

        if (!defaultCategoryId || defaultCategoryId <= 0) {
            setError('Keine gültige Skill-Kategorie gefunden. Bitte kontaktieren Sie den Administrator.');
            return;
        }

        if (!skillType) {
            setError('Bitte wählen Sie einen Skill-Typ aus');
            return;
        }

        setSaving(true);
        setError('');

        try {
            console.log('Creating skill with params:', {
                title: title.trim(),
                description: description.trim() || 'Selbst erstellter Skill',
                categoryId: defaultCategoryId,
                otherNames: [],
                skillType,
                productName: '',
                manufacturer: '',
                link: '',
                acquisition: 'Selbststudium/Berufserfahrung',
                acquisitionLink: '',
            });

            const newSkill = await createSkill(
                title.trim(),
                description.trim() || 'Selbst erstellter Skill',
                defaultCategoryId,
                [], // otherNames
                skillType,
                '', // productName
                '', // manufacturer
                '', // link
                'Selbststudium/Berufserfahrung', // acquisition
                '', // acquisitionLink
            );

            console.log('Skill created successfully:', newSkill);
            // Skill created globally, user will rate it separately
            onSkillCreated(newSkill);
            handleClose();
        } catch (err) {
            console.error('Skill creation error:', err);
            const errorMessage =
                (err as any)?.message || (err as Error)?.toString() || 'Unbekannter Fehler beim Erstellen des Skills';
            setError(`Fehler beim Erstellen des Skills: ${errorMessage}`);
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        setTitle('');
        setDescription('');
        setSkillType(ESkillType.HARD_SKILL);
        setError('');
        onClose();
    };

    if (!open) return null;

    return (
        <Dialog
            id="CreateSimpleSkillDialog"
            title="Neuen Skill erstellen"
            onClose={handleClose}
            footer={
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button onClick={handleClose} disabled={saving}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={saving || offline || !title.trim() || !defaultCategoryId || !skillType}
                    >
                        {saving ? 'Wird erstellt...' : 'Erstellen'}
                    </Button>
                </Box>
            }
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Hand size={20} />
                <Typography variant="h6">Neuen Skill erstellen</Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                    required
                    id="skill-name-field"
                    label="Skill-Name"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    fullWidth
                    disabled={saving || offline}
                    error={!!error && !title.trim()}
                    InputLabelProps={{ shrink: true }}
                    helperText="Geben Sie einen eindeutigen Namen für den Skill ein"
                />

                <TextField
                    id="skill-description-field"
                    label="Beschreibung"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    disabled={saving || offline}
                    placeholder="Kurze Beschreibung des Skills (optional)"
                    InputLabelProps={{ shrink: true }}
                    helperText="Optionale Beschreibung zur Erläuterung des Skills"
                />

                <FormControl fullWidth disabled={saving || offline}>
                    <InputLabel>Skill-Typ</InputLabel>
                    <Select
                        value={skillType}
                        onChange={(e) => setSkillType(e.target.value as ESkillType)}
                        label="Skill-Typ"
                    >
                        <MenuItem value={ESkillType.HARD_SKILL}>Hard-Skill</MenuItem>
                        <MenuItem value={ESkillType.SOFT_SKILL}>Soft-Skill</MenuItem>
                        <MenuItem value={ESkillType.LANGUAGE_SKILL}>Sprach-Skill</MenuItem>
                    </Select>
                </FormControl>

                {error && (
                    <Typography color="error" variant="body2">
                        {error}
                    </Typography>
                )}

                {offline && (
                    <Typography color="warning" variant="body2">
                        Sie sind offline. Bitte prüfen Sie Ihre Internetverbindung.
                    </Typography>
                )}

                <Typography variant="caption" color="text.secondary">
                    Debug: Kategorie-ID: {defaultCategoryId}, Skill-Typ: {skillType}, Kategorien verfügbar:{' '}
                    {skillCategories.length}
                </Typography>
            </Box>
        </Dialog>
    );
}
